import { useMemo } from 'react'

import {
  MIN_QUERY_LENGTH,
  useCarrierSearch as useRegistrySearch,
  type FmcsaSearchCompany,
} from 'src/components/Inputs/CarrierAutocomplete/useCarrierSearch'

import type { CarrierChoice } from './entryFlow'

export { MIN_QUERY_LENGTH }

export interface CarrierSearch {
  results: CarrierChoice[]
  searching: boolean
  /** The registry did not answer. Different from it answering with nothing. */
  failed: boolean
  /** True once a query has actually been run for the current text. */
  searched: boolean
  /** The query as the server searched it, for telemetry. Null until known. */
  normalizedQuery: string | null
  /** The query was read as a DOT number and looked up by it. */
  dotLookup: boolean
}

const toChoice = (company: FmcsaSearchCompany): CarrierChoice => ({
  name: company.companyName ?? '',
  dotNumber: company.dotNumber ?? null,
  city: company.address?.city ?? null,
  state: company.address?.state ?? null,
  street: company.address?.street ?? null,
  powerUnits: company.powerUnits ?? null,
  inDirectory: company.inDirectory ?? null,
})

/** The registry search, shaped for the driver sheet's entry flow. */
export const useCarrierSearch = (query: string): CarrierSearch => {
  const search = useRegistrySearch(query)
  const results = useMemo(
    () => search.results.map(toChoice).filter((carrier) => carrier.name),
    [search.results]
  )
  return {
    results,
    searching: search.searching,
    failed: search.failed,
    searched: search.searched,
    normalizedQuery: search.normalizedQuery,
    dotLookup: search.dotLookup,
  }
}
